"use client"

import * as React from "react"
import { Tooltip as TooltipPrimitive } from "@base-ui/react/tooltip"

import { cn } from "@/shared/lib/utils"

type TooltipSide = "top" | "right" | "bottom" | "left"

function TooltipProvider({
  delay = 150,
  ...props
}: TooltipPrimitive.Provider.Props) {
  return <TooltipPrimitive.Provider delay={delay} {...props} />
}

function Tooltip(props: TooltipPrimitive.Root.Props) {
  return <TooltipPrimitive.Root {...props} />
}

function TooltipTrigger(props: TooltipPrimitive.Trigger.Props) {
  return <TooltipPrimitive.Trigger data-slot="tooltip-trigger" {...props} />
}

function TooltipContent({
  className,
  side = "top",
  sideOffset = 8,
  children,
  ...props
}: TooltipPrimitive.Popup.Props & {
  side?: TooltipSide
  sideOffset?: number
}) {
  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Positioner
        side={side}
        sideOffset={sideOffset}
        collisionPadding={12}
      >
        <TooltipPrimitive.Popup
          data-slot="tooltip-content"
          className={cn(
            // Sized for 50+ readability: 14px body, generous line-height and
            // padding, brand dark green surface with cream text (high contrast),
            // soft elevation. Wraps (max-w) rather than clipping.
            "z-50 max-w-[300px] rounded-[10px] bg-[var(--green)] px-4 py-3 font-sans text-[14px] font-normal leading-[1.5] text-[var(--bg)] shadow-[0_8px_28px_-6px_rgba(26,26,24,0.35)]",
            className,
          )}
          {...props}
        >
          {children}
        </TooltipPrimitive.Popup>
      </TooltipPrimitive.Positioner>
    </TooltipPrimitive.Portal>
  )
}

/**
 * Convenience wrapper: attaches a styled hover tooltip to a single element.
 * Pass the trigger element as ``children`` (it becomes the hover target via
 * Base UI's ``render`` composition); ``content`` is the popup text. Renders
 * the child untouched when ``content`` is empty.
 */
/**
 * A tooltip that only names an icon button. Same brand surface as the default
 * so tooltips stay one thing, but scaled to the control it labels: a one-word
 * label needs neither the reading-comfort type nor the elevation of a popup
 * carrying real content.
 */
const COMPACT_POPUP =
  "max-w-[220px] rounded-[6px] px-2 py-1 text-[11px] leading-[1.35] " +
  "shadow-[0_2px_8px_-2px_rgba(26,26,24,0.25)]"

function SimpleTooltip({
  content,
  children,
  side,
  delay = 100,
  defaultOpen,
  compact = false,
}: {
  content: React.ReactNode
  children: React.ReactElement
  side?: TooltipSide
  delay?: number
  defaultOpen?: boolean
  /**
   * Naming an icon button rather than explaining something. A one-word action
   * label does not need the reading-comfort sizing the default carries, and at
   * that size it dwarfs the control it belongs to.
   */
  compact?: boolean
}) {
  if (!content) return children
  return (
    // `delay` / `closeDelay` are Provider props - Root accepts neither, so
    // passing them there set no delay at all. Each SimpleTooltip renders its
    // own Provider, so this stays a per-tooltip timing, not a global one.
    <TooltipProvider delay={delay} closeDelay={0}>
      <Tooltip defaultOpen={defaultOpen}>
        <TooltipTrigger render={children} />
        <TooltipContent side={side} className={compact ? COMPACT_POPUP : undefined}>{content}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

export {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
  SimpleTooltip,
}
